import { initAnimationsForElement } from '../utils/animations.js';

const PRESETS = {
  fashion: { price: 90, margin: 55, churn: 8.0, cac: 120, new_cust: 200 },
  consumables: { price: 45, margin: 40, churn: 12.0, cac: 60, new_cust: 500 },
  health_beauty: { price: 65, margin: 75, churn: 6.5, cac: 95, new_cust: 300 },
};

const SUBMIT_ENDPOINT = '/api/submit-unit-economics-lead';

class UnitEconomicsSimulator extends HTMLElement {
  #elements = {};
  #activePreset = 'health_beauty';
  #lastMetrics = null;
  #isSubmitting = false;

  connectedCallback() {
    this.#cacheElements();
    this.#bindEvents();
    this.loadPreset('health_beauty');

    requestAnimationFrame(() => {
      initAnimationsForElement(this);
    });
  }

  #cacheElements() {
    const ids = [
      'price', 'price_num', 'margin', 'margin_num',
      'churn', 'churn_num', 'cac', 'cac_num', 'new_cust', 'new_cust_num',
      'display_ltv', 'display_lifespan', 'display_payback', 'display_ratio',
      'ratio_status_badge', 'health_meter_label', 'gauge_pointer',
      'display_plateau', 'display_mrr', 'display_mprofit', 'display_arr',
      'diagnostic_box', 'curve_path', 'curve_fill',
      'print_date', 'print_assessment', 'print_ltv',
      'print_ratio', 'print_payback', 'print_lifespan', 'print_requestor_name',
      'print_shopify_url', 'print_name', 'print_email', 'print_shopify_url_input',
      'print_company_website', 'print_submit_status',
      'tt_print_modal', 'tt_print_form',
      'p_in_price', 'p_in_margin', 'p_in_churn', 'p_in_cac', 'p_in_new',
      'p_ceil_subs', 'p_ceil_mrr', 'p_ceil_profit', 'p_ceil_arr',
      'p_out_payback', 'print_analysis',
    ];

    for (const id of ids) {
      this.#elements[this.#toCamelCase(id)] = this.querySelector(`#${id}`);
    }
  }

  #toCamelCase(id) {
    return id.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  }

  #bindEvents() {
    const pairs = [
      { slider: this.#elements.price, num: this.#elements.priceNum },
      { slider: this.#elements.margin, num: this.#elements.marginNum },
      { slider: this.#elements.churn, num: this.#elements.churnNum },
      { slider: this.#elements.cac, num: this.#elements.cacNum },
      { slider: this.#elements.newCust, num: this.#elements.newCustNum },
    ];

    for (const pair of pairs) {
      if (!pair.slider || !pair.num) continue;

      pair.num.value = pair.slider.value;

      pair.slider.addEventListener('input', (event) => {
        pair.num.value = event.target.value;
        this.calculateEconomics();
      });

      pair.num.addEventListener('input', (event) => {
        let val = parseFloat(event.target.value);
        if (Number.isNaN(val)) return;

        const min = parseFloat(pair.slider.min);
        const max = parseFloat(pair.slider.max);
        val = Math.min(Math.max(val, min), max);

        pair.slider.value = String(val);
        pair.num.value = pair.slider.value;
        this.calculateEconomics();
      });
    }

    this.querySelector('[data-action="print-report"]')?.addEventListener('click', () => {
      this.#openPrintModal();
    });

    this.#elements.ttPrintForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.#handlePrintSubmit();
    });

    for (const button of this.querySelectorAll('[data-action="close-print-modal"]')) {
      button.addEventListener('click', () => {
        this.#elements.ttPrintModal?.close();
      });
    }

    this.#elements.ttPrintModal?.addEventListener('click', (event) => {
      if (event.target === this.#elements.ttPrintModal) {
        this.#elements.ttPrintModal.close();
      }
    });

    window.addEventListener('afterprint', () => {
      document.body.classList.remove('tt-printing');
    });

    for (const button of this.querySelectorAll('[data-preset]')) {
      button.addEventListener('click', () => {
        this.loadPreset(button.dataset.preset);
      });
    }
  }

  loadPreset(key) {
    const preset = PRESETS[key];
    if (!preset) return;

    this.#activePreset = key;
    this.#elements.price.value = preset.price;
    this.#elements.priceNum.value = this.#elements.price.value;
    this.#elements.margin.value = preset.margin;
    this.#elements.marginNum.value = this.#elements.margin.value;
    this.#elements.churn.value = preset.churn;
    this.#elements.churnNum.value = this.#elements.churn.value;
    this.#elements.cac.value = preset.cac;
    this.#elements.cacNum.value = this.#elements.cac.value;
    this.#elements.newCust.value = preset.new_cust;
    this.#elements.newCustNum.value = this.#elements.newCust.value;

    for (const button of this.querySelectorAll('[data-preset]')) {
      const isActive = button.dataset.preset === key;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    this.calculateEconomics();
  }

  #syncRangeFills() {
    const sliders = [
      this.#elements.price,
      this.#elements.margin,
      this.#elements.churn,
      this.#elements.cac,
      this.#elements.newCust,
    ];

    for (const slider of sliders) {
      if (!slider) continue;
      const min = Number(slider.min);
      const max = Number(slider.max);
      const value = Number(slider.value);
      const progress = max === min ? 0 : ((value - min) / (max - min)) * 100;
      slider.style.setProperty('--range-progress', `${progress}%`);
    }
  }

  calculateEconomics() {
    this.#syncRangeFills();

    const price = parseFloat(this.#elements.price.value);
    const margin = parseFloat(this.#elements.margin.value) / 100;
    const churn = parseFloat(this.#elements.churn.value) / 100;
    const cac = parseFloat(this.#elements.cac.value);
    const newCust = parseFloat(this.#elements.newCust.value);

    const grossProfitPerSub = price * margin;
    const customerLifespan = 1 / churn;
    const ltv = grossProfitPerSub * customerLifespan;
    const ratio = cac > 0 ? ltv / cac : Infinity;
    const payback = grossProfitPerSub > 0 ? cac / grossProfitPerSub : Infinity;

    const plateau = newCust / churn;
    const steadyStateMRR = plateau * price;
    const steadyStateGrossProfit = plateau * grossProfitPerSub;
    const steadyStateARR = steadyStateMRR * 12;

    this.#elements.displayLtv.textContent = `$${Math.round(ltv).toLocaleString()}`;
    this.#elements.displayLifespan.textContent = `~${customerLifespan.toFixed(1)} month average lifespan`;
    this.#elements.displayPayback.textContent = payback === Infinity ? '0.0 mo' : `${payback.toFixed(1)} mo`;
    this.#elements.displayRatio.textContent = ratio === Infinity ? '∞' : `${ratio.toFixed(1)}x`;
    this.#elements.displayPlateau.textContent = `${Math.round(plateau).toLocaleString()} active subscribers`;
    this.#elements.displayMrr.textContent = `$${Math.round(steadyStateMRR).toLocaleString()}`;
    this.#elements.displayMprofit.textContent = `$${Math.round(steadyStateGrossProfit).toLocaleString()}`;
    this.#elements.displayArr.textContent = `$${Math.round(steadyStateARR).toLocaleString()}`;

    const gaugeStatus = this.#updateGauge(ratio);
    this.#updateGraph(newCust, churn, plateau);
    this.#generateAdvisory(ratio, payback, gaugeStatus.statusKey);

    this.#lastMetrics = {
      ratio,
      payback,
      ltv,
      customerLifespan,
      price,
      margin,
      churn,
      cac,
      newCust,
      plateau,
      steadyStateMRR,
      steadyStateGrossProfit,
      steadyStateARR,
      gaugeStatus,
    };

    this.#syncPrintLayout(this.#lastMetrics);
  }

  #updateGauge(ratio) {
    let statusText = '';
    let statusKey = 'danger';
    let pointerLeft = 0;

    if (ratio < 2.0) {
      statusText = 'Sub-optimal';
      statusKey = 'danger';
      pointerLeft = Math.min((ratio / 2.0) * 33.3, 33.3);
    } else if (ratio < 3.5) {
      statusText = 'Viable';
      statusKey = 'warning';
      pointerLeft = 33.3 + ((ratio - 2.0) / 1.5) * 33.3;
    } else {
      statusText = 'Exceptional';
      statusKey = 'success';
      const bonusPoints = ratio === Infinity ? 33.3 : ((ratio - 3.5) / 5.0) * 33.3;
      pointerLeft = 66.6 + Math.min(bonusPoints, 31.4);
    }

    this.#elements.ratioStatusBadge.className = `tt-status tt-status--${statusKey}`;
    this.#elements.ratioStatusBadge.textContent = statusText;
    this.#elements.healthMeterLabel.textContent = `${statusText} (${ratio === Infinity ? 'Infinite' : `${ratio.toFixed(1)}x`})`;
    this.#elements.healthMeterLabel.className = `tt-status-label tt-status-label--${statusKey}`;
    this.#elements.gaugePointer.style.left = `calc(${pointerLeft}% - 5px)`;

    return { statusText, statusKey };
  }

  #updateGraph(newCustomers, churnRate, plateau) {
    const months = 12;
    const points = [];
    let active = 0;

    for (let month = 0; month <= months; month += 1) {
      if (month === 0) {
        active = 0;
      } else {
        active = active * (1 - churnRate) + newCustomers;
      }
      points.push({ month, count: active });
    }

    const maxVal = Math.max(plateau * 1.1, points[months].count * 1.1, 100);
    const width = 500;
    const height = 200;

    let pathD = '';
    let polyPoints = `0,${height} `;

    points.forEach((point, index) => {
      const x = (point.month / months) * width;
      const y = height - (point.count / maxVal) * height;

      pathD += index === 0 ? `M ${x} ${y} ` : `L ${x} ${y} `;
      polyPoints += `${x},${y} `;
    });

    polyPoints += `${width},${height}`;

    this.#elements.curvePath.setAttribute('d', pathD);
    this.#elements.curveFill.setAttribute('points', polyPoints);
  }

  #generateAdvisory(ratio, payback, statusKey) {
    let diagnostic = '';

    if (statusKey === 'danger') {
      diagnostic = `
        <strong>Critical growth action required:</strong><br>
        Unit economics are currently highly compressed. At a ratio of ${ratio === Infinity ? 'unbounded' : `${ratio.toFixed(1)}x`}, scaling customer acquisition spend will quickly result in cash-flow deficits rather than profitable growth.
        <ul class="tt-diagnostic-list">
          <li><strong>ARPU optimization:</strong> Consider tier structures, add-ons, or a price increase to boost the average contract size.</li>
          <li><strong>Churn defense:</strong> Address client drop-off immediately. Reducing monthly cancellations by even a small percentage yields immediate, compounding multipliers on LTV.</li>
        </ul>
      `;
    } else if (statusKey === 'warning') {
      diagnostic = `
        <strong>Balanced model: proceed with targeted scaling</strong><br>
        The business model exhibits a healthy foundation. The CAC payback period of ${payback.toFixed(1)} months is within standard operational standards.
        <ul class="tt-diagnostic-list">
          <li><strong>Channel refinement:</strong> Audit paid marketing funnels to drive organic referrals and lower blended CAC below current levels.</li>
          <li><strong>Retention mechanics:</strong> Introduce targeted onboarding or value milestones inside the customer lifecycle to lock in longer lifetime terms.</li>
        </ul>
      `;
    } else {
      diagnostic = `
        <strong>Exceptional performance profile: scale aggressively</strong><br>
        The financial structure represents a highly efficient subscription loop. With a payback speed of only ${payback.toFixed(1)} months, the company recovers quickly and has a highly viable margin safety window.
        <ul class="tt-diagnostic-list">
          <li><strong>Aggressive deployment:</strong> Reinvest excess cash flow back into customer acquisition channels. This model can support much higher CAC to accelerate scale.</li>
          <li><strong>Market dominance:</strong> Focus on expansion strategies and upsell systems to increase expansion MRR from the existing customer cohort.</li>
        </ul>
      `;
    }

    this.#elements.diagnosticBox.className = `tt-status tt-status--${statusKey} margin-top-20`;
    this.#elements.diagnosticBox.innerHTML = diagnostic;
  }

  #syncPrintLayout(data) {
    const { statusText, statusKey } = data.gaugeStatus;

    this.#elements.printDate.textContent = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    this.#elements.printAssessment.textContent = statusText;
    this.#elements.printAssessment.className = `tt-status tt-status--${statusKey}`;

    this.#elements.printLtv.textContent = `$${Math.round(data.ltv).toLocaleString()}`;
    this.#elements.printLtv.className = 'tt-print-highlight';
    this.#elements.printRatio.textContent = data.ratio === Infinity ? 'Infinite' : `${data.ratio.toFixed(1)}x`;
    this.#elements.printRatio.className = 'tt-print-highlight';
    this.#elements.printPayback.textContent = data.payback === Infinity ? '0.0 mo' : `${data.payback.toFixed(1)} Months`;
    this.#elements.printLifespan.textContent = `${data.customerLifespan.toFixed(1)} Months`;

    this.#elements.pInPrice.textContent = `$${data.price.toLocaleString()}`;
    this.#elements.pInMargin.textContent = `${(data.margin * 100).toFixed(0)}%`;
    this.#elements.pInChurn.textContent = `${(data.churn * 100).toFixed(1)}%`;
    this.#elements.pInCac.textContent = `$${data.cac.toLocaleString()}`;
    this.#elements.pInNew.textContent = `${data.newCust.toLocaleString()} /mo`;

    this.#elements.pCeilSubs.textContent = `${Math.round(data.plateau).toLocaleString()}`;
    this.#elements.pCeilMrr.textContent = `$${Math.round(data.steadyStateMRR).toLocaleString()}`;
    this.#elements.pCeilProfit.textContent = `$${Math.round(data.steadyStateGrossProfit).toLocaleString()}`;
    this.#elements.pCeilArr.textContent = `$${Math.round(data.steadyStateARR).toLocaleString()}`;
    this.#elements.pOutPayback.textContent = data.payback === Infinity ? '0.0 Months' : `${data.payback.toFixed(1)} Months`;

    this.#elements.printAnalysis.className = `tt-print-analysis-body tt-status tt-status--${statusKey}`;
    this.#elements.printAnalysis.innerHTML = this.#elements.diagnosticBox.innerHTML;
  }

  #openPrintModal() {
    const modal = this.#elements.ttPrintModal;
    if (!modal) return;

    modal.showModal();
    this.#elements.printName?.focus();
  }

  async #handlePrintSubmit() {
    if (this.#isSubmitting) return;

    const form = this.#elements.ttPrintForm;
    const urlInput = this.#elements.printShopifyUrlInput;

    if (urlInput?.value.trim()) {
      urlInput.value = this.#normalizeShopifyUrl(urlInput.value.trim());
    }

    if (!form?.checkValidity()) {
      form?.reportValidity();
      return;
    }

    if (this.#elements.printCompanyWebsite?.value.trim()) {
      return;
    }

    const name = this.#elements.printName.value.trim();
    const email = this.#elements.printEmail.value.trim();
    const shopifyUrl = this.#elements.printShopifyUrlInput.value.trim();

    this.#setSubmitStatus('');
    this.#isSubmitting = true;
    this.#setSubmitButtonState(true);

    try {
      await this.#submitToAttio({ name, email, shopifyUrl });
    } catch (error) {
      console.error('Unit economics simulator Attio submission failed:', error);
      this.#setSubmitStatus('We saved your report locally, but CRM sync failed. You can still print.');
    }

    this.#elements.printRequestorName.textContent = name;
    this.#elements.printShopifyUrl.textContent = shopifyUrl;

    this.#elements.ttPrintModal?.close();
    this.#isSubmitting = false;
    this.#setSubmitButtonState(false);
    document.body.classList.add('tt-printing');
    window.print();
  }

  async #submitToAttio({ name, email, shopifyUrl }) {
    if (!this.#lastMetrics) return;

    const { firstName, lastName } = this.#splitName(name);
    const { gaugeStatus } = this.#lastMetrics;

    const response = await fetch(SUBMIT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'unit-economics-simulator',
        tool: 'Unit Economics Simulator',
        name,
        first_name: firstName,
        last_name: lastName,
        email,
        shopify_url: shopifyUrl,
        shopify_domain: this.#extractDomain(shopifyUrl),
        preset: this.#activePreset,
        arpu: this.#lastMetrics.price,
        margin_pct: Math.round(this.#lastMetrics.margin * 100),
        churn_pct: Number((this.#lastMetrics.churn * 100).toFixed(1)),
        cac: this.#lastMetrics.cac,
        new_customers_per_month: this.#lastMetrics.newCust,
        ltv: Math.round(this.#lastMetrics.ltv),
        ltv_cac_ratio: this.#lastMetrics.ratio === Infinity
          ? null
          : Number(this.#lastMetrics.ratio.toFixed(1)),
        payback_months: this.#lastMetrics.payback === Infinity
          ? null
          : Number(this.#lastMetrics.payback.toFixed(1)),
        lifespan_months: Number(this.#lastMetrics.customerLifespan.toFixed(1)),
        plateau_subscribers: Math.round(this.#lastMetrics.plateau),
        mrr_ceiling: Math.round(this.#lastMetrics.steadyStateMRR),
        profit_ceiling: Math.round(this.#lastMetrics.steadyStateGrossProfit),
        arr_ceiling: Math.round(this.#lastMetrics.steadyStateARR),
        viability_status: gaugeStatus.statusText,
        page_url: window.location.href,
        submitted_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Lead submission failed with ${response.status}`);
    }
  }

  #setSubmitButtonState(isSubmitting) {
    const button = this.querySelector('[data-action="submit-print-report"]');
    if (!button) return;

    button.disabled = isSubmitting;
    button.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  }

  #setSubmitStatus(message) {
    const status = this.#elements.printSubmitStatus;
    if (!status) return;

    if (!message) {
      status.hidden = true;
      status.textContent = '';
      return;
    }

    status.hidden = false;
    status.textContent = message;
  }

  #splitName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  }

  #extractDomain(url) {
    try {
      return new URL(url).hostname.replace(/^www\./i, '');
    } catch {
      return '';
    }
  }

  #normalizeShopifyUrl(url) {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('//')) return `https:${url}`;
    return `https://${url}`;
  }
}

customElements.define('unit-economics-simulator', UnitEconomicsSimulator);
